import produce from 'immer';
import create from 'zustand';
import { devtools } from 'zustand/middleware';

import { devToolsAllowed } from 'constants/devtools';
import {
  ADD_CLIP,
  REMOVE_CLIP,
  REMOVE_PLAY_RANGE,
  REPOSITION_CLIPS,
  TRIM_CHANGE_MADE,
} from 'constants/events';
import { MIN_PLAY_RANGE_DURATION } from 'constants/trimming';
import * as analytics from 'utilities/analytics';
import * as loggerx from 'utilities/loggerx';
import {
  getSortedPlayRanges,
  getTrimVideoDuration,
  makePlayRangeId,
  makePlayRangesFromTrimRanges,
  makeTrimRangesFromPlayRanges,
} from 'utilities/trimming';

import { PlayRange } from 'types/playRanges';

import { getClipSourceUrl } from './util';
import {
  Clip,
  VideoClipAttributes,
} from '../../../../../../shared/types/clips';

export interface UndoEntry {
  clips: Clip[];
  removedClipIds?: string[];
}

export interface RecentlyAddedInfoType {
  name: string;
  clipIds: string[];
}

const ADD = 'add';
const RESET = 'reset';
const REPLACE = 'replace';
const UNDO_ACTIONS = [ADD, RESET, REPLACE] as const;
const RECENTLY_ADDED_TIMEOUT = 10000;

type UndoActionType = typeof UNDO_ACTIONS[number];

export interface ClipsState {
  clips: Clip[];

  removedClipIds: string[];
  hasBeenUpdated: boolean;
  focusedClipIndex: number;
  updateFocusedClipIndex: (idx: number) => void;
  focusedPlayRangeIndex: number;
  updateFocusedPlayRangeIndex: (idx: number) => void;
  addClips: (nextClips: Clip[], options?: { initial: boolean }) => void;
  addClip: (
    clip: Clip,
    options?: { undoAction: UndoActionType }
  ) => Promise<boolean | undefined>;
  removeClip: (idx: number) => void;
  repositionClip: (startIdx: number, endIdx: number) => void;
  updatePlayRangeForClip: (
    clipIdx: number
  ) => ({ id, from, to }: { id: string; from: number; to: number }) => void;
  removePlayRangeFromClip: (clipIdx: number, playRangeIdx: number) => void;
  replacePlayRangesForClip: (
    clipIdx: number,
    newPlayRanges: PlayRange[]
  ) => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
  canDeletePlayRange: () => boolean;
  getDuration: () => number;
  hasTrims: () => boolean;
  getTrimVideoDuration: () => number;
  getCurrentClip: () => Clip | undefined;
  getCurrentPlayRanges: () => PlayRange[];
  undoStack: UndoEntry[];
  undoPointer: number;
  undo: (videoId: string) => void;
  redo: (videoId: string) => void;
  resetState: () => void;
  updateCurrentClipDuration: (newDuration: number) => void;
  splitCurrentPlayRange: (currentTime: number) => void;
  setClips: (clips: VideoClipAttributes[]) => void;
  recentlyAddedInfo: RecentlyAddedInfoType | null;
  setRecentlyAddedInfo: (info: RecentlyAddedInfoType) => void;
  clearRecentlyAddedInfo: () => void;
}

const byPosition = (a, b) => a.position - b.position;

export const initialState = {
  clips: [],
  removedClipIds: [],
  hasBeenUpdated: false,
  focusedClipIndex: 0,
  focusedPlayRangeIndex: 0,
  undoStack: [],
  undoPointer: -1,
  recentlyAddedInfo: null,
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const stateFn = (set, get): ClipsState => {
  // unexported, helper functions
  const addToUndoStack = (payload: UndoEntry) => {
    const removedClipIds = payload.removedClipIds || get().removedClipIds;
    const clips = payload.clips;

    set(
      {
        undoStack: [
          ...get().undoStack.slice(0, get().undoPointer + 1),
          { clips, removedClipIds },
        ],
        undoPointer: get().undoPointer + 1,
      },
      false,
      'addToUndoStack'
    );
  };

  const replaceLastUndo = (payload: UndoEntry) => {
    const removedClipIds = payload.removedClipIds || get().removedClipIds;
    const clips = payload.clips;

    set(
      {
        undoStack: [
          ...get().undoStack.slice(0, get().undoPointer),
          { clips, removedClipIds },
        ],
      },
      false,
      'replaceLastUndo'
    );
  };

  const resetUndoStack = (entry: UndoEntry) =>
    set({ undoStack: [entry], undoPointer: 0 }, false, 'resetUndoStack');

  // public interface
  return {
    ...initialState,

    resetState: () => set({ ...initialState }, false, 'resetState'),

    updateFocusedClipIndex: idx =>
      set({ focusedClipIndex: idx }, false, 'updateFocusedClipIndex'),

    updateFocusedPlayRangeIndex: idx =>
      set({ focusedPlayRangeIndex: idx }, false, 'updateFocusedPlayRangeIndex'),

    addClips: async (nextClips, { initial } = { initial: false }) => {
      const prevClipsIds = get().clips.map(clip => clip.id);
      const removedClipIds = get().removedClipIds;

      // don't add clips we already have in the store
      nextClips = nextClips.filter(clip => !prevClipsIds.includes(clip.id));

      // if the clip has been already removed from the video, don't add it back
      nextClips = nextClips.filter(clip => !removedClipIds.includes(clip.id));

      // Add the source urls all at one time.
      nextClips = await Promise.all(nextClips.map(addClipSourceUrlToClip));

      for (let idx = 0; idx < nextClips.length; idx++) {
        const clip = nextClips[idx];

        if (initial) {
          await get().addClip(clip, { undoAction: RESET });
        } else {
          await get().addClip(clip, {
            undoAction: idx === 0 ? ADD : REPLACE,
          });
        }
      }
    },

    addClip: async (
      clip,
      { undoAction }: { undoAction: UndoActionType } = {
        undoAction: ADD,
      }
    ) => {
      const clips = get().clips;

      // don't add clips we already have in the store
      if (clips.some(c => c.id === clip.id)) {
        return;
      }

      // if the clip has been already removed from the video, don't add it back
      if (get().removedClipIds.some(id => id === clip.id)) {
        return;
      }

      await transformClipForPlay(clip);

      const nextClips = [...get().clips, clip];
      const nextRemovedClipIds = [...get().removedClipIds];

      const undoEntry = {
        clips: nextClips,
        removedClipIds: nextRemovedClipIds,
      };

      if (undoAction === RESET) {
        resetUndoStack(undoEntry);
      } else if (undoAction === REPLACE) {
        replaceLastUndo(undoEntry);
      } else {
        addToUndoStack(undoEntry);
      }

      analytics.track(ADD_CLIP, {
        clipId: clip.id,
        rootVideoId: clip.root_video_id,
      });

      set(
        {
          clips: nextClips,
          hasBeenUpdated: undoAction !== RESET,
          focusedClipIndex: undoAction === RESET ? 0 : nextClips.length - 1,
          focusedPlayRangeIndex: 0,
        },
        false,
        'addClip'
      );

      return true;
    },

    removeClip: idx => {
      if (idx >= get().clips.length || idx < 0) {
        return;
      }

      if (get().clips.length === 1) {
        // do not allow removing the last clip in the video
        const err = new Error('Cannot remove the last clip in a video');

        loggerx.error(
          err,
          {
            message: 'Failed to remove clip',
            clipId: get().clips[0].id,
            videoId: get().clips[0].root_video_id,
          },
          {
            team: loggerx.CREATE_TEAM,
          }
        );
        throw err;
      }

      let removedId;

      const nextClips = produce<VideoClipAttributes[]>(
        get().clips,
        (draftClips: VideoClipAttributes[]) => {
          const [removedClip] = draftClips.splice(idx, 1);

          removedId = removedClip.id;

          // fix the position of the clips after the removed one
          for (let draftIdx = idx; draftIdx < draftClips.length; draftIdx++) {
            draftClips[draftIdx].position = draftIdx + 1;
          }

          analytics.track(REMOVE_CLIP, {
            clipId: removedClip.id,
            rootVideoId: removedClip.root_video_id,
          });
        }
      );

      const nextRemovedIds = [...get().removedClipIds, removedId];

      addToUndoStack({
        clips: nextClips,
        removedClipIds: nextRemovedIds,
      });

      set(
        {
          clips: nextClips,
          removedClipIds: nextRemovedIds,
          focusedClipIndex: Math.min(
            get().focusedClipIndex,
            nextClips.length - 1
          ),
          focusedPlayRangeIndex: 0,
          hasBeenUpdated: true,
        },
        false,
        'removeClip'
      );
    },

    repositionClip: (startIdx, endIdx) => {
      if (
        startIdx === endIdx ||
        startIdx >= get().clips.length ||
        endIdx >= get().clips.length
      ) {
        return;
      }

      const nextClips = produce<VideoClipAttributes[]>(
        get().clips,
        (draftClips: Array<VideoClipAttributes>) => {
          const [clip] = draftClips.splice(startIdx, 1); // remove

          analytics.track(REPOSITION_CLIPS, {
            clipId: clip.id,
            rootVideoId: clip.root_video_id,
            startIdx,
            endIdx,
          });

          draftClips.splice(endIdx, 0, clip); // insert

          for (let idx = 0; idx < draftClips.length; idx++) {
            draftClips[idx].position = idx + 1;
          }
        }
      );

      addToUndoStack({ clips: nextClips });

      set(
        {
          clips: nextClips,
          hasBeenUpdated: true,
        },
        false,
        'repositionClip'
      );
    },

    updatePlayRangeForClip:
      clipIdx =>
      ({ id: rangeId, from, to }) => {
        if (clipIdx >= get().clips.length || clipIdx < 0) {
          return;
        }

        const nextClips = produce<Clip[]>(get().clips, draftClips => {
          const draftClip = draftClips[clipIdx];
          const playRanges = draftClip.playRanges as PlayRange[];
          const playRangeIdx = playRanges.findIndex(({ id }) => rangeId === id);
          const prevPlayRange = playRanges[playRangeIdx];

          prevPlayRange.from = from;
          prevPlayRange.to = to;

          // ensure the play ranges stay sorted
          draftClip.playRanges = getSortedPlayRanges(playRanges);

          draftClip.trimRanges = makeTrimRangesFromPlayRanges(
            draftClip.playRanges,
            draftClip.duration
          );

          return draftClips;
        });

        addToUndoStack({ clips: nextClips });

        set(
          { clips: nextClips, hasBeenUpdated: true },
          false,
          'updatePlayRangeForClip'
        );
      },

    removePlayRangeFromClip: (clipIdx: number, playRangeIdx: number) => {
      if (clipIdx < 0) {
        return;
      }

      const clip = get().clips[clipIdx];

      if (clip.playRanges.length <= 1) {
        // do not allow removing the last play range, should use removeClip() instead
        const err = new Error('Cannot remove the last play range in a clip');

        loggerx.error(
          err,
          {
            message: 'Failed to remove play range',
            clipId: clip.id,
            videoId: clip.root_video_id,
          },
          {
            team: loggerx.CREATE_TEAM,
          }
        );
        throw err;
      }

      const nextClips = produce<Clip[]>(get().clips, draftClips => {
        const draftClip = draftClips[clipIdx];
        const playRanges = draftClip.playRanges as PlayRange[];

        if (playRangeIdx < playRanges.length) {
          playRanges.splice(playRangeIdx, 1);

          draftClip.trimRanges = makeTrimRangesFromPlayRanges(
            draftClip.playRanges,
            draftClip.duration
          );
        }

        analytics.track(REMOVE_PLAY_RANGE, {
          clipId: draftClip.id,
          rootVideoId: draftClip.root_video_id,
        });

        return draftClips;
      });

      addToUndoStack({ clips: nextClips });

      const nextPlayRanges = nextClips[clipIdx].playRanges as PlayRange[];

      set(
        {
          clips: nextClips,
          hasBeenUpdated: true,
          focusedPlayRangeIndex: Math.min(
            get().focusedPlayRangeIndex,
            nextPlayRanges.length - 1
          ),
        },
        false,
        'removePlayRangeFromClip'
      );
    },

    replacePlayRangesForClip: (clipIdx, newPlayRanges) => {
      if (
        clipIdx >= get().clips.length ||
        clipIdx < 0 ||
        newPlayRanges.length < 1
      ) {
        return;
      }

      const nextClips = produce<Clip[]>(get().clips, draftClips => {
        const draftClip = draftClips[clipIdx];

        draftClip.playRanges = getSortedPlayRanges(newPlayRanges);
        draftClip.trimRanges = makeTrimRangesFromPlayRanges(
          draftClip.playRanges,
          draftClip.duration
        );

        return draftClips;
      });

      addToUndoStack({ clips: nextClips });

      set(
        { clips: nextClips, hasBeenUpdated: true },
        false,
        'replacePlayRangesForClip'
      );
    },

    canUndo: () => get().undoPointer > 0,
    canRedo: () => get().undoPointer < get().undoStack.length - 1,
    canDeletePlayRange: () =>
      get().clips.length > 1 || get().clips[0]?.playRanges.length > 1,

    getDuration: () =>
      get().clips.reduce((acc, clip) => acc + clip.duration, 0),
    hasTrims: () => get().clips.some(clip => clip.trimRanges.length > 0),
    getTrimVideoDuration: () =>
      get().clips.reduce(
        (acc, clip) =>
          acc + getTrimVideoDuration(clip.duration, clip.trimRanges),
        0
      ),
    getCurrentClip: () => get().clips[get().focusedClipIndex],
    getCurrentPlayRanges: () =>
      get().clips[get().focusedClipIndex]?.playRanges || [],

    undo: videoId => {
      const nextPointer = get().undoPointer - 1;

      if (get().undoStack[nextPointer]) {
        analytics.track(TRIM_CHANGE_MADE, {
          id: videoId,
          type: 'undo',
          hotkey: false,
          event_type: 'click',
        });

        set(
          {
            clips: [...get().undoStack[nextPointer].clips],
            removedClipIds: [...get().undoStack[nextPointer].removedClipIds],
            undoPointer: nextPointer,
            hasBeenUpdated: nextPointer > 0,
          },
          false,
          'undo'
        );
      }
    },

    redo: videoId => {
      const nextPointer = get().undoPointer + 1;

      if (get().undoStack[nextPointer]) {
        analytics.track(TRIM_CHANGE_MADE, {
          id: videoId,
          type: 'redo',
          hotkey: false,
          event_type: 'click',
        });

        set(
          {
            clips: [...get().undoStack[nextPointer].clips],
            removedClipIds: [...get().undoStack[nextPointer].removedClipIds],
            undoPointer: nextPointer,
            hasBeenUpdated: nextPointer > 0,
          },
          false,
          'redo'
        );
      }
    },

    updateCurrentClipDuration: newDuration => {
      const clipIdx = get().focusedClipIndex;

      const nextClips = produce<Clip[]>(get().clips, draftClips => {
        if (draftClips[clipIdx]) {
          draftClips[clipIdx].duration = newDuration;
          draftClips[clipIdx].playRanges = makePlayRangesFromTrimRanges(
            draftClips[clipIdx].trimRanges,
            newDuration
          );
        }

        return draftClips;
      });

      set({ clips: nextClips }, false, 'updateCurrentClipDuration');
    },

    splitCurrentPlayRange: currentTime => {
      const playRangeIdx = get().focusedPlayRangeIndex;
      const playRanges = get().getCurrentPlayRanges();
      const { from, to } = playRanges[playRangeIdx];
      const duration = to - from;

      if (duration < 2 * MIN_PLAY_RANGE_DURATION) {
        // too short to be split
        return;
      } else if (currentTime - from < MIN_PLAY_RANGE_DURATION) {
        currentTime = from + MIN_PLAY_RANGE_DURATION;
      } else if (to - currentTime < MIN_PLAY_RANGE_DURATION) {
        currentTime = to - MIN_PLAY_RANGE_DURATION;
      }

      const newSplitPlayRanges = [
        {
          id: makePlayRangeId(),
          from,
          to: currentTime,
        },
        {
          id: makePlayRangeId(),
          from: currentTime,
          to,
        },
      ];

      const newPlayRanges = playRanges
        .slice(0, playRangeIdx)
        .concat(newSplitPlayRanges, playRanges.slice(playRangeIdx + 1));

      get().replacePlayRangesForClip(get().focusedClipIndex, newPlayRanges);
    },

    setClips: async clips => {
      const state = get();

      const oldClipIds = state.clips.map(c => c.id);
      const removedClipIds = [
        ...new Set([...state.removedClipIds, ...oldClipIds]),
      ];

      const newClips = clips.filter(clip => {
        return !removedClipIds.some(id => id === clip.id);
      });

      // Ensure positions are correctly orderer and start from 1
      newClips.sort(byPosition);
      newClips.forEach((c, i) => (c.position = i + 1));

      await Promise.all([...newClips.map(transformClipForPlay)]);

      addToUndoStack({
        clips: newClips,
        removedClipIds,
      });

      set(
        {
          clips: newClips,
          removedClipIds,
          focusedClipIndex: 0,
          focusedPlayRangeIndex: 0,
          hasBeenUpdated: true,
        },
        false,
        'setClips'
      );
    },

    setRecentlyAddedInfo: recentlyAddedInfo => {
      set({ recentlyAddedInfo }, false, 'setRecentlyAddedInfo');
      setTimeout(() => {
        get().clearRecentlyAddedInfo();
      }, RECENTLY_ADDED_TIMEOUT);
    },
    clearRecentlyAddedInfo: () =>
      set({ recentlyAddedInfo: null }, false, 'clearRecentlyAddedInfo'),
  };
};

async function addClipSourceUrlToClip(clip: Clip) {
  // TODO: set forceOriginal to true if the clip's source s3 id matches that of the video's s3 id
  const forceOriginal = false;

  const {
    // @ts-expect-error ignore due to enabling strict null checks
    source_url,
    // @ts-expect-error ignore due to enabling strict null checks
    source_mime_type,
    // @ts-expect-error ignore due to enabling strict null checks
    source_url_is_transcoded,
    // @ts-expect-error ignore due to enabling strict null checks
    source_part_credentials,
  } = await getClipSourceUrl({
    clip,
    forceOriginal,
  });

  // adding a query param to the clip source url if there are
  // 2 clips from the same source video id that may have the
  // same source_url
  clip.source_url = source_url || null;
  clip.source_mime_type = source_mime_type || null;
  clip.source_url_is_transcoded = source_url_is_transcoded || false;
  clip.source_part_credentials = source_part_credentials || null;

  return clip;
}

async function transformClipForPlay(clip: Clip) {
  if (!clip.source_url) {
    await addClipSourceUrlToClip(clip);
  }

  clip.trimRanges = clip.bounded_trim_ranges || [];
  clip.duration = clip.video_properties.duration;

  clip.playRanges =
    makePlayRangesFromTrimRanges(clip.trimRanges, clip.duration) || [];

  return clip;
}

export const useClipsState = devToolsAllowed
  ? create<ClipsState>(devtools(stateFn, 'ClipsState'))
  : create<ClipsState>(stateFn);
